class ACModernHome {
  constructor(root) {
    this.root = root;
    this.cards = [...root.querySelectorAll('.ac-product-card')];
    this.filterButtons = [...root.querySelectorAll('[data-ac-filter]')];
    this.grid = root.querySelector('[data-ac-grid]');
    this.empty = root.querySelector('[data-ac-empty]');
    this.categoryTimers = [];
    this.bindFilters();
    this.initHeroBanner();
    this.initMediaRotation();
    this.observeSections();
  }

  initHeroBanner() {
    const banner = this.root.querySelector('[data-ac-hero]');
    if (!banner) return;

    const pool = banner.querySelector('.ac-hero__pool');
    const track = banner.querySelector('[data-ac-hero-track]');
    const dots = banner.querySelector('[data-ac-hero-dots]');
    if (!pool || !track || !dots) return;

    const candidates = [...pool.querySelectorAll('[data-ac-hero-slide]')];
    const categoryGroups = [...candidates.reduce((groups, slide) => {
      const category = slide.dataset.acHeroCategory;
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(slide);
      return groups;
    }, new Map()).values()];
    const selectedGroups = [...categoryGroups];
    const slides = [];

    while (slides.length < 3 && selectedGroups.length) {
      const weights = selectedGroups.map((group) => 2 ** (7 - Number(group[0].dataset.acHeroPriority || 6)));
      const totalWeight = weights.reduce((total, weight) => total + weight, 0);
      let draw = Math.random() * totalWeight;
      let selectedIndex = 0;
      for (let index = 0; index < weights.length; index += 1) {
        draw -= weights[index];
        if (draw <= 0) {
          selectedIndex = index;
          break;
        }
      }
      const [selectedGroup] = selectedGroups.splice(selectedIndex, 1);
      slides.push(shuffleACNavProducts(selectedGroup)[0]);
    }

    if (slides.length < 3) {
      const remaining = candidates.filter((candidate) => !slides.includes(candidate));
      slides.push(...shuffleACNavProducts(remaining).slice(0, 3 - slides.length));
    }
    slides.sort((first, second) => Number(first.dataset.acHeroPriority) - Number(second.dataset.acHeroPriority));
    if (!slides.length) {
      banner.remove();
      return;
    }

    slides.forEach((slide, index) => {
      slide.dataset.acHeroIndex = index;
      slide.setAttribute('aria-label', `${index + 1} de ${slides.length}`);
      track.appendChild(slide);

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Mostrar destaque ${index + 1}`);
      dot.addEventListener('click', () => showSlide(index, true));
      dots.appendChild(dot);
    });
    pool.remove();

    const dotButtons = [...dots.querySelectorAll('button')];
    let activeIndex = 0;
    let timer;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const showSlide = (nextIndex, restart = false) => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a').forEach((link) => link.tabIndex = active ? 0 : -1);
      });
      dotButtons.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });
      if (restart) startTimer();
    };

    const stopTimer = () => window.clearInterval(timer);
    const startTimer = () => {
      stopTimer();
      if (!reducedMotion && slides.length > 1) timer = window.setInterval(() => showSlide(activeIndex + 1), 2500);
    };

    banner.querySelector('[data-ac-hero-prev]')?.addEventListener('click', () => showSlide(activeIndex - 1, true));
    banner.querySelector('[data-ac-hero-next]')?.addEventListener('click', () => showSlide(activeIndex + 1, true));
    banner.addEventListener('mouseenter', stopTimer);
    banner.addEventListener('mouseleave', startTimer);
    banner.addEventListener('focusin', stopTimer);
    banner.addEventListener('focusout', (event) => {
      if (!banner.contains(event.relatedTarget)) startTimer();
    });

    slides.forEach((slide, index) => {
      slide.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = index === 0 ? 'high' : 'low';
      });
    });
    banner.dataset.acHeroReady = 'true';
    showSlide(0);
    startTimer();
    this.categoryTimers.push(stopTimer);
  }

  initMediaRotation() {
    this.root.querySelectorAll('[data-ac-category-media]').forEach((media, mediaIndex) => {
      const frames = [...media.querySelectorAll('.ac-category__frame')];
      if (frames.length < 2) return;

      let activeIndex = Math.floor(Math.random() * frames.length);
      const showFrame = () => {
        frames.forEach((frame, index) => frame.classList.toggle('is-active', index === activeIndex));
        media.classList.add('is-randomized');
      };
      const advance = () => {
        activeIndex = (activeIndex + 1) % frames.length;
        showFrame();
      };
      showFrame();

      let timer = window.setInterval(advance, 4600 + (mediaIndex * 320));
      media.closest('.ac-category').addEventListener('mouseenter', () => window.clearInterval(timer));
      media.closest('.ac-category').addEventListener('mouseleave', () => {
        window.clearInterval(timer);
        timer = window.setInterval(advance, 4600 + (mediaIndex * 320));
      });
      this.categoryTimers.push(() => window.clearInterval(timer));
    });

    this.cards.forEach((card) => {
      if (card.querySelector('.reveal .hidden') && Math.random() > 0.5) card.classList.add('is-alt-start');
    });
  }

  destroy() {
    this.productRequest?.abort();
    this.categoryTimers.forEach((clearTimer) => clearTimer());
  }

  bindFilters() {
    this.paging = this.root.querySelector('[data-ac-paging]');
    this.productSection = this.root.querySelector('[data-ac-products]');
    if (!this.paging || !this.grid) return;
    this.paging.hidden = false;
    this.pageIndex = 0;
    this.pageCount = 0;
    this.filterButtons.forEach((button) => button.addEventListener('click', () => this.filter(button)));
    this.paging.querySelector('[data-ac-page-prev]').addEventListener('click', () => this.goToPage(this.pageIndex - 1));
    this.paging.querySelector('[data-ac-page-next]').addEventListener('click', () => {
      if (this.pageIndex + 1 < this.pageCount) this.goToPage(this.pageIndex + 1);
      else this.loadProductPage(this.pendingProductReset);
    });
    this.grid.addEventListener('scroll', () => {
      if (!this.grid.classList.contains('ac-product-pages')) return;
      this.pageIndex = Math.round(this.grid.scrollLeft / this.grid.clientWidth);
      this.updatePaging();
    }, { passive: true });
    this.filter(this.filterButtons[0]);
  }

  filter(button) {
    this.productRequest?.abort();
    this.productRequest = new AbortController();
    this.filterButtons.forEach((item) => {
      item.classList.toggle('is-active', item === button);
      item.setAttribute('aria-pressed', String(item === button));
    });
    this.under50 = button?.dataset.acFilter === 'under-50';
    this.nextProductUrl = new URL(button?.dataset.acCollectionUrl || this.productSection.dataset.acCollectionUrl, location.origin);
    this.nextProductUrl.searchParams.set('view', 'ac-paging');
    this.productSection.querySelector('.ac-products__heading a')?.setAttribute('href', this.nextProductUrl.pathname);
    this.pendingProductReset = true;
    this.loadProductPage(true);
  }

  async loadProductPage(reset) {
    if (!this.nextProductUrl) return;
    const controller = this.productRequest;
    this.productsLoading = true;
    this.pagingError = false;
    this.grid.setAttribute('aria-busy', 'true');
    this.updatePaging();
    try {
      let next = this.nextProductUrl;
      const cards = [];
      let totalPages = 0;
      do {
        const response = await fetch(next, { signal: controller.signal });
        if (!response.ok) throw new Error('Product page unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const page = doc.querySelector('[data-ac-page]');
        if (!page) throw new Error('Product page missing');
        totalPages = Number(page.dataset.pages);
        cards.push(...[...page.querySelectorAll('.ac-product-card')].filter((card) => !this.under50 || Number(card.dataset.price) <= 5000));
        next = page.dataset.next ? new URL(page.dataset.next, location.origin) : null;
        next?.searchParams.set('view', 'ac-paging');
      } while (next && this.under50 && cards.length < 6);
      if (controller.signal.aborted) return;
      if (reset) {
        this.grid.replaceChildren();
        this.grid.classList.add('ac-product-pages');
        this.grid.scrollLeft = 0;
        this.pageCount = 0;
        this.pageIndex = 0;
      }
      const destination = this.pageCount;
      for (let index = 0; index < cards.length; index += 6) {
        const page = document.createElement('div');
        page.className = 'ac-product-page';
        page.setAttribute('role', 'group');
        page.setAttribute('aria-label', `Página ${++this.pageCount}`);
        page.append(...cards.slice(index, index + 6));
        this.grid.appendChild(page);
      }
      this.pendingProductReset = false;
      this.nextProductUrl = next;
      this.productTotalPages = this.under50 ? null : totalPages;
      this.empty.hidden = this.pageCount > 0;
      // Quick view clicks are delegated by the theme; do not bind them again.
      if (!reset && cards.length) this.goToPage(destination);
    } catch (error) {
      if (error.name !== 'AbortError') this.pagingError = true;
    } finally {
      if (controller === this.productRequest) {
        this.productsLoading = false;
        this.grid.setAttribute('aria-busy', 'false');
        this.updatePaging();
      }
    }
  }

  goToPage(index) {
    this.pageIndex = Math.max(0, Math.min(index, this.pageCount - 1));
    this.grid.scrollTo({ left: this.pageIndex * this.grid.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    this.updatePaging();
  }

  updatePaging() {
    this.paging.querySelector('[data-ac-page-prev]').disabled = this.productsLoading || this.pageIndex === 0;
    this.paging.querySelector('[data-ac-page-next]').disabled = this.productsLoading || (this.pageIndex + 1 >= this.pageCount && !this.nextProductUrl);
    this.paging.querySelector('[data-ac-page-status]').textContent = this.productsLoading ? 'A carregar…' : this.pagingError ? 'Não foi possível carregar. Tente novamente →' : this.pageCount ? `Página ${this.pageIndex + 1}${this.productTotalPages ? ` de ${this.productTotalPages}` : ''}` : 'Sem produtos';
  }

  observeSections() {
    const sections = this.root.querySelectorAll('.ac-reveal');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sections.forEach((section) => section.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    sections.forEach((section) => observer.observe(section));
  }
}

const initACModern = (scope = document) => {
  scope.querySelectorAll('[data-ac-home]').forEach((root) => {
    if (root.dataset.acReady) return;
    root.dataset.acReady = 'true';
    root.acModernInstance = new ACModernHome(root);
  });
};

const initQuickviewBackdrop = () => {
  const modal = document.getElementById('modal-quickview-cart');
  if (!modal || modal.dataset.acBackdropReady) return;

  modal.dataset.acBackdropReady = 'true';
  modal.addEventListener('click', (event) => {
    if (event.target !== modal) return;
    const closeButton = modal.querySelector('.js-modal-close');
    if (closeButton) closeButton.click();
  });
};



const shuffleACNavProducts = (items) => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }
  return shuffled;
};

const initACNavProductMenus = (scope = document) => {
  scope.querySelectorAll('[data-ac-nav-products]').forEach((menu) => {
    if (menu.dataset.acNavReady) return;

    const pool = menu.querySelector('.ac-nav-products__pool');
    const list = menu.querySelector('[data-ac-nav-list]');
    const feature = menu.querySelector('[data-ac-nav-feature]');
    if (!pool || !list || !feature) return;

    const products = shuffleACNavProducts(pool.querySelectorAll('[data-ac-nav-item]'));
    const featuredProduct = products.shift();
    if (!featuredProduct) {
      menu.remove();
      return;
    }

    featuredProduct.classList.add('is-featured');
    feature.appendChild(featuredProduct);
    products.slice(0, 3).forEach((product) => list.appendChild(product));
    pool.remove();

    menu.querySelectorAll('img').forEach((image) => {
      image.loading = 'eager';
      image.fetchPriority = 'low';
    });
    menu.querySelectorAll('a[tabindex="-1"]').forEach((link) => link.removeAttribute('tabindex'));
    menu.dataset.acNavReady = 'true';
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initACModern();
  initQuickviewBackdrop();
  initACNavProductMenus();
});
document.addEventListener('shopify:section:load', (event) => {
  initACModern(event.target);
  initACNavProductMenus(event.target);
});
document.addEventListener('shopify:section:unload', (event) => event.target.querySelector('[data-ac-home]')?.acModernInstance?.destroy());
window.addEventListener('scroll', () => document.body.classList.toggle('ac-page-scrolled', window.scrollY > 10), { passive: true });
